import React, { useEffect, useState, useCallback } from 'react';
import NewPost from './NewPost';
import PostItem from './PostItem';
import { authFetch, unAuthFetch } from '../../Utilities/Api';
import { useAuthContext } from '../../context/AuthContext';
import './ProfilePostsFeed.css';

const ProfilePostsFeed = ({ profileUserId, isOwnProfile }) => {
    const { user: viewer } = useAuthContext();

    const [posts, setPosts] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [isPrivateOrEmpty, setIsPrivateOrEmpty] = useState(false);

    // 🗑️ REMOVED: Parent-level state for comments. PostItem handles this now.

    const fetchPosts = useCallback(async (pageToLoad = 1, replace = false) => {
        setLoading(true);
        const fetcher = viewer ? authFetch : unAuthFetch;

        try {
            const res = await fetcher(`/profile-posts/user/${profileUserId}?page=${pageToLoad}`);
            const data = await res.json();

            if (!res.ok) {
                if (res.status === 403) setIsPrivateOrEmpty(true);
                throw new Error(data.message || `Status: ${res.status}`);
            }

            setPosts(prev => replace ? data : [...prev, ...data]);
            setPage(pageToLoad);
            setHasMore(data.length === 10);
            setIsPrivateOrEmpty(pageToLoad === 1 && data.length === 0);

        } catch (err) {
            console.error('❌ Failed to fetch posts:', err);
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    }, [profileUserId, viewer]);

    useEffect(() => {
        if (!profileUserId) return setInitialLoading(false);
        fetchPosts(1, true);
    }, [profileUserId, fetchPosts]);

    const handleLoadMore = () => {
        if (hasMore && !loading) fetchPosts(page + 1);
    };

    const handlePostCreated = (newPost) => {
        const completePost = newPost.author?.username ? newPost : {
            ...newPost,
            author: {
                _id: viewer._id,
                username: viewer.username,
                profilePicture: viewer.profilePicture
            }
        };

        setPosts(prev => [completePost, ...prev]);
        setIsPrivateOrEmpty(false);
    };

    const handlePostDeleted = (postId) => {
        setPosts(prev => {
            const updated = prev.filter(post => post._id !== postId);
            if (updated.length === 0) setIsPrivateOrEmpty(true);
            return updated;
        });
    };

    // 🗑️ REMOVED: toggleComments function

    if (initialLoading) return <div className="profile-posts"><p className="loading-msg">Loading posts...</p></div>;

    return (
        <div className="profile-posts">
            {isOwnProfile && <NewPost onPostCreated={handlePostCreated} />}

            {posts.length === 0 ? (
                <div className="no-posts-container">
                    <p className="no-posts-msg">
                        {isOwnProfile
                            ? "You haven't posted anything yet. Start posting!"
                            : isPrivateOrEmpty
                                ? "This profile currently has no posts visible to you."
                                : "No posts yet."}
                    </p>
                </div>
            ) : (
                <div className="posts-list">
                    {posts.map(post => (
                        <PostItem
                            key={post._id}
                            post={post}
                            onPostDeleted={handlePostDeleted}
                            // ✅ FIXED: Always allow comments feature, let PostItem manage the toggle
                            showComments={true}
                        />
                    ))}

                    {hasMore && (
                        <button onClick={handleLoadMore} disabled={loading} className="load-more-btn">
                            {loading ? 'Loading...' : 'Load more'}
                        </button>
                    )}
                </div>
            )}
            {!initialLoading && loading && page > 1 && <p className="loading-msg">Loading more...</p>}
        </div>
    );
};

export default ProfilePostsFeed;